import { useReminderHook } from "@/hooks/use-reminder.hook.ts";
import { ApiNotificationChannelType } from "@/types/notification.types.ts";
import { ReminderMode, ReminderPeriod, ReminderSettings } from "@/types/reminder.types.ts";
import {
  Box,
  Button,
  FontAwesomeIcon,
  ListItem,
  Multiselect,
  Radio,
  Select,
  Stack,
  Tag,
  Text,
  Title,
} from "@skailer-com/uikit";
import { ReactNode, useState } from "react";
import {
  Control,
  Controller,
  FieldPathValue,
  FieldValues,
  Path,
  useWatch,
  UseFormSetValue,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

interface WorkflowReminderSettingsFormShape {
  reminders_enabled: boolean;
  reminder_settings: ReminderSettings;
}

interface WorkflowReminderSettingsSectionProps<
  T extends FieldValues & WorkflowReminderSettingsFormShape,
> {
  control: Control<T>;
  setValue: UseFormSetValue<T>;
  hasSmartBase: boolean;
  smartBaseValue: number;
  requiredText: string;
  renderHeader: (toggle: { checked: boolean; onChange: (checked: boolean) => void }) => ReactNode;
}

const fallbackReminderSettings: ReminderSettings = {
  channels: [
    ApiNotificationChannelType.in_app,
    ApiNotificationChannelType.email,
    ApiNotificationChannelType.slack,
  ],
  mode: ReminderMode.medium,
  custom_subject: null,
  custom_body: null,
  custom_frequency: null,
  custom_period: null,
};

export function WorkflowReminderSettingsSection<
  T extends FieldValues & WorkflowReminderSettingsFormShape,
>({
  control,
  setValue,
  hasSmartBase,
  smartBaseValue,
  requiredText,
  renderHeader,
}: WorkflowReminderSettingsSectionProps<T>) {
  const { t } = useTranslation();
  const {
    getReminderModeTitle,
    getReminderModeDescription,
    getReminderSettingsDescription,
    getReminderPeriodTitle,
    getReminderEveryTitle,
    calculateReminderCount,
    getReminderModeTagVariant,
    getNotificationChannelTitle,
  } = useReminderHook();

  const [isChangingMode, setIsChangingMode] = useState(false);
  const remindersEnabledName = "reminders_enabled" as Path<T>;
  const reminderSettingsName = "reminder_settings" as Path<T>;
  const remindersEnabled = useWatch({ control, name: remindersEnabledName }) ?? false;
  const reminderSettings =
    (useWatch({ control, name: reminderSettingsName }) as ReminderSettings | undefined) ??
    fallbackReminderSettings;

  const reminderCount = calculateReminderCount(
    smartBaseValue,
    reminderSettings.mode ?? ReminderMode.medium
  );

  const externalChannelOptions = [
    {
      id: ApiNotificationChannelType.email,
      title: getNotificationChannelTitle(ApiNotificationChannelType.email),
    },
    {
      id: ApiNotificationChannelType.slack,
      title: getNotificationChannelTitle(ApiNotificationChannelType.slack),
    },
    {
      id: ApiNotificationChannelType.messenger,
      title: getNotificationChannelTitle(ApiNotificationChannelType.messenger),
    },
  ];

  function setReminderSettings(nextSettings: ReminderSettings) {
    setValue(reminderSettingsName, nextSettings as FieldPathValue<T, typeof reminderSettingsName>);
  }

  function ensureCustomReminderSettings() {
    setReminderSettings({
      ...reminderSettings,
      mode: ReminderMode.custom,
      custom_frequency: reminderSettings.custom_frequency ?? 1,
      custom_period: reminderSettings.custom_period ?? ReminderPeriod.day,
    });
  }

  function handleReminderToggleChange(checked: boolean, onChange: (checked: boolean) => void) {
    onChange(checked);

    if (checked && hasSmartBase) {
      setReminderSettings({
        ...reminderSettings,
        mode: ReminderMode.medium,
      });
    }

    if (checked && !hasSmartBase) {
      setReminderSettings({
        ...reminderSettings,
        mode: ReminderMode.custom,
        custom_frequency: 1,
        custom_period: ReminderPeriod.day,
      });
    }

    if (!checked) {
      setIsChangingMode(false);
    }
  }

  return (
    <Controller
      control={control}
      name={remindersEnabledName}
      render={({ field: { onChange } }) => (
        <>
          {renderHeader({
            checked: remindersEnabled,
            onChange: (checked) => handleReminderToggleChange(checked, onChange),
          })}

          {remindersEnabled && (
            <Box className="py-4 px-6 bg-dark/5 border-dark/5 rounded-xs border">
              {!isChangingMode ? (
                <ListItem
                  className="py-3"
                  behavior={"card"}
                  title={t("core:reminder_mode", {
                    level: getReminderModeTitle(reminderSettings.mode),
                  })}
                  captionSlot={
                    <Stack>
                      <Text>{getReminderSettingsDescription(reminderSettings)}</Text>
                      <Text className="text-secondary">
                        {reminderSettings.channels
                          .map((channel) => getNotificationChannelTitle(channel))
                          .join(", ")}
                      </Text>
                    </Stack>
                  }
                  valueSlot={
                    <Button variant="secondary" size="sm" onClick={() => setIsChangingMode(true)}>
                      {t("common:change")}
                    </Button>
                  }
                />
              ) : (
                <Stack>
                  <Stack gap={"md"}>
                    <Title
                      header={t("core:choose_reminder_frequency")}
                      size="xs"
                      caption={t("tasks:overdue_alerts_sent_regardless")}
                    />

                    {!hasSmartBase && (
                      <Stack direction={"horizontal"}>
                        <Tag variant={"secondary"}>
                          <Stack direction={"horizontal"} gap={"xs"} items={"center"}>
                            <FontAwesomeIcon icon="fa-thin fa-circle-info" />
                            {t("core:reminder_requires", { needed: requiredText })}
                          </Stack>
                        </Tag>
                      </Stack>
                    )}

                    <Controller
                      control={control}
                      name={"reminder_settings.mode" as Path<T>}
                      render={({ field: { value, onChange: onModeChange } }) => (
                        <Stack gap="sm">
                          {[
                            ReminderMode.light,
                            ReminderMode.medium,
                            ReminderMode.hard,
                            ReminderMode.custom,
                          ].map((item) => (
                            <Radio
                              disabled={!hasSmartBase && item !== ReminderMode.custom}
                              key={`radio-${item}`}
                              value={item}
                              checked={value === item}
                              onChange={() => {
                                if (item === ReminderMode.custom) {
                                  ensureCustomReminderSettings();
                                  return;
                                }

                                onModeChange(item);
                              }}
                              title={getReminderModeTitle(item)}
                              caption={getReminderModeDescription(item)}
                            />
                          ))}

                          {value === ReminderMode.custom && (
                            <Stack
                              direction="horizontal"
                              gap="sm"
                              items="center"
                              className={"pl-7"}
                            >
                              <Text>
                                {getReminderEveryTitle(
                                  reminderSettings.custom_period ?? ReminderPeriod.day,
                                  reminderSettings.custom_frequency ?? 1
                                )}
                              </Text>
                              <Controller
                                control={control}
                                name={"reminder_settings.custom_frequency" as Path<T>}
                                render={({
                                  field: { value: freqValue, onChange: freqOnChange },
                                }) => (
                                  <Select
                                    variant="button"
                                    emptyTitle={t("common:period")}
                                    value={String(freqValue ?? 1)}
                                    onChange={(value) => freqOnChange(Number(value))}
                                    options={Array.from({ length: 6 }, (_, index) => ({
                                      id: String(index + 1),
                                      title: String(index + 1),
                                    }))}
                                  />
                                )}
                              />
                              <Controller
                                control={control}
                                name={"reminder_settings.custom_period" as Path<T>}
                                render={({
                                  field: { value: periodValue, onChange: periodOnChange },
                                }) => (
                                  <Select
                                    key={`custom-period-${reminderSettings.custom_frequency ?? 1}`}
                                    variant="button"
                                    value={periodValue ?? ReminderPeriod.day}
                                    onChange={periodOnChange}
                                    emptyTitle={t("common:period")}
                                    options={[
                                      {
                                        id: ReminderPeriod.day,
                                        title: getReminderPeriodTitle(
                                          ReminderPeriod.day,
                                          reminderSettings.custom_frequency ?? 1
                                        ),
                                      },
                                      {
                                        id: ReminderPeriod.week,
                                        title: getReminderPeriodTitle(
                                          ReminderPeriod.week,
                                          reminderSettings.custom_frequency ?? 1
                                        ),
                                      },
                                      {
                                        id: ReminderPeriod.month,
                                        title: getReminderPeriodTitle(
                                          ReminderPeriod.month,
                                          reminderSettings.custom_frequency ?? 1
                                        ),
                                      },
                                    ]}
                                  />
                                )}
                              />
                            </Stack>
                          )}
                        </Stack>
                      )}
                    />

                    {(smartBaseValue > 0 || reminderSettings.mode === ReminderMode.custom) && (
                      <Stack direction={"horizontal"}>
                        <Tag variant={getReminderModeTagVariant(reminderSettings.mode)}>
                          <Stack direction={"horizontal"} items={"center"} gap={"xs"}>
                            <FontAwesomeIcon icon="fa-light fa-bell" />
                            {reminderSettings.mode === ReminderMode.custom
                              ? getReminderSettingsDescription(reminderSettings)
                              : t("core:reminder_count_info", {
                                  reminders: t("plurals:reminders_count", {
                                    count: reminderCount,
                                  }),
                                  days: t("plurals:plural_day", {
                                    count: smartBaseValue,
                                  }),
                                })}
                          </Stack>
                        </Tag>
                      </Stack>
                    )}
                  </Stack>

                  <Title header={t("core:external_channels")} size="xs" paddingTop />

                  <Controller
                    control={control}
                    name={"reminder_settings.channels" as Path<T>}
                    render={({ field: { value, onChange: onChannelsChange } }) => (
                      <Multiselect
                        label={t("common:type")}
                        emptyTitle={t("common:select")}
                        values={
                          ((value as ApiNotificationChannelType[]) ?? []).filter(
                            (channel) => channel !== ApiNotificationChannelType.in_app
                          ) as string[]
                        }
                        options={externalChannelOptions}
                        onChange={(channels) =>
                          onChannelsChange([ApiNotificationChannelType.in_app, ...channels])
                        }
                      />
                    )}
                  />
                </Stack>
              )}
            </Box>
          )}
        </>
      )}
    />
  );
}
